import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  softseeUsedAppsPageQuery,
  softseeUsedAppsTableQuery,
  type SoftseeUsedAppRow,
  type SoftseeUsedAppsTable,
} from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { SoftseeToolbar } from './SoftseeToolbar'
import type { ScreenProps } from './screens'
import './DeviceSoftseeUsedApps.css'

/**
 * Fila Used Applications - softsee/devices/details/usedApplications.php:
 * tile-ul Most Used, Period (Today / 7 days / 30 days, 7 selectat) si tabelul
 * DeviceUsedApplicationsModel (Name, Focus Time, Last Access). Init-ul de pe
 * cec-master e [[1, 'desc']] pe Focus Time, cum sorteaza si SQL-ul, care
 * ignora order-ul. Cautarea e a barii DataTables; Period sta langa ea, pe
 * acelasi rand (`SoftseeToolbar`).
 *
 * Most Used vine cu fiecare pagina a listei (`most_used`), ca `xhr.dt` din
 * vechea macheta: varful perioadei alese, fara cautare.
 */

const TABLE = 'deviceSoftseeUsedApps'

export function DeviceSoftseeUsedApps({ id }: ScreenProps) {
  const page = useQuery(softseeUsedAppsPageQuery(id))
  const [period, setPeriod] = useState<1 | 7 | 30>(7)
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)

  const table = useQuery({
    ...softseeUsedAppsTableQuery(id, { start, length, search, periodDays: period }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-softsee-used-apps">
      <div className="row">
        <div className="col-md-12">
          <MostUsed table={table.data} />
          <DetailsTable
            rows={table.data?.data ?? []}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            search={search}
            start={start}
            length={length}
            onSearch={(q) => {
              setSearch(q)
              setStart(0)
            }}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            period={
              /*
               * Period sta in randul cautarii, langa ea, nu pe un rand de formular
               * deasupra listei: doua unelte care taie aceeasi lista, pe un rand -
               * cum le poarta si celelalte liste din portal.
               *
               * Nicio latime scrisa de mana. Pe randul de formular (`FormGrid`),
               * unde a stat pana acum, campul lua coloana intreaga (627px la
               * 1600px); aici, sub `form-inline`-ul invelisului, `.form-control`
               * iese `inline-block` si cat ii cere continutul - ca selectul "Show
               * 10 entries" din subsol. Vechile `min-width: 140` si `width: auto`
               * scrise in `style` nu se intorc: erau ocolirea randului de formular.
               */
              <div className="softsee-toolbar-field">
                <label htmlFor="usedAppsPeriod">Period</label>
                <select
                  id="usedAppsPeriod"
                  className="form-control"
                  value={period}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    setPeriod(n === 1 || n === 30 ? n : 7)
                    setStart(0)
                  }}
                >
                  <option value={1}>Today</option>
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
            }
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Tile-ul Most Used. Pana vine primul raspuns scrie linia si fraza de
 * lamurire a machetei; o perioada fara mostre spune asta, nu lasa numele gol.
 */
function MostUsed({ table }: { table: SoftseeUsedAppsTable | undefined }) {
  const app = table?.most_used ?? null
  const meta = !table
    ? 'Same ranking as Core Application (Focus Time).'
    : app
      ? `Focus Time ${app.focus_time}`
      : 'No focus samples in this period yet.'
  return (
    <div id="usedAppsMostUsed" className="used-apps-most-used">
      <div className="used-apps-most-used-label">Most Used</div>
      <div id="usedAppsMostUsedName" className="used-apps-most-used-name">
        {app ? app.name : '–'}
      </div>
      <div id="usedAppsMostUsedMeta" className="used-apps-most-used-meta">
        {meta}
      </div>
    </div>
  )
}

/* Focus Time e coloana dupa care se sorteaza lista - [[1, 'desc']]. */
const SORTED = 1

const COLUMNS: { key: keyof SoftseeUsedAppRow; title: string }[] = [
  { key: 'name', title: 'Name' },
  { key: 'focus_time', title: 'Focus Time' },
  { key: 'last_access', title: 'Last Access' },
]

function DetailsTable({
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  onSearch,
  onStart,
  onLength,
  period,
}: {
  rows: SoftseeUsedAppRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  /** Selectul Period, pus in randul cautarii. */
  period: ReactNode
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)

  useEffect(() => {
    setDraft(search)
  }, [search])

  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <SoftseeToolbar>
        <div className="dataTableF">
          <div id={`${TABLE}_filter`} className="dataTables_filter">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onSearch(value), 400)
                }}
              />
            </label>
          </div>
        </div>
        {period}
      </SoftseeToolbar>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable applications-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => (
                  <th
                    key={col.key}
                    className={`td-small a-left${i === SORTED ? ' sorting_desc' : ' sorting'}`}
                    aria-label={`${col.title}: activate to sort column ascending`}
                    aria-sort={i === SORTED ? 'descending' : undefined}
                  >
                    {col.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={3} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={3} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {COLUMNS.map((col) => (
                      <td key={col.key} className="td-small a-left">
                        {row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
